import { computed, ref, Ref, SetupContext } from '@vue/composition-api';
import { AcoFormData, AdgroupFormData, CREATIVE_TYPE, FullNormalFormData, NormalCreative } from '@tt4b/ttam-enums';
import { cloneDeep } from 'lodash-es';
import { getFormDataByNormalCreativeData, queryToJson } from '@tt4b/ttam-utils';

export function useSmartTextToolsForm(opt: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  emit: SetupContext['emit'];
}) {
  const { adgroupData, creativeData, emit } = opt;
  // Config
  const MAX_TITLE_COUNT = 1;
  const MAX_ACO_TITLE_COUNT = 5;
  const isAco = computed(() => adgroupData.value.creativeMaterialMode === CREATIVE_TYPE.PROCEDURAL);
  const existingTexts = computed(() => {
    if (isAco.value) {
      return creativeData.value.titleList.map(item => item.title).filter(item => item) || [];
    }
    return [creativeData.value.title];
  });

  const maxNum = computed(() => {
    if (isAco.value) {
      const currentTitleCount = existingTexts.value.length || 0;
      return MAX_ACO_TITLE_COUNT - currentTitleCount;
    }
    return MAX_TITLE_COUNT;
  });

  /**
   * 回填选中的智能文案
   * 1. 普通创意：智能文案 和 智能翻译 都是替换原来的标题
   * 2. ACO，标题为一个列表：
   *  - 智能文案：新增到原本的标题列表后
   *  - 智能翻译：替换原本的标题列表文案
   *
   * @param selectedTitles 选中的标题
   * @param isTranslation 是否为智能翻译
   */
  function setText(selectedTitles, isTranslation = false) {
    if (!selectedTitles?.length) {
      return;
    }
    if (!isAco.value) {
      const selectedTitle = selectedTitles[0];
      emit('creativeData-change', {
        title: selectedTitle.originTitle || '',
        titleSmartTextInfo: selectedTitle,
      });
      return;
    }
    let newTitleList = cloneDeep(creativeData.value.titleList || []);
    if (isTranslation) {
      selectedTitles.forEach((item, index) => {
        newTitleList[index] = { title: item.originTitle, titleSmartTextInfo: item };
      });
    } else {
      // 把选中的文案先填上空的标题
      newTitleList = newTitleList.map(item => {
        if (!item.title && selectedTitles.length) {
          const selectedItem = selectedTitles.shift();
          item.title = selectedItem.originTitle;
          item.titleSmartTextInfo = selectedItem;
        }
        return item;
      });
      newTitleList = newTitleList.concat(
        selectedTitles.map(item => ({
          title: item.originTitle,
          titleSmartTextInfo: item,
        }))
      );
    }
    emit('creativeData-change', { titleList: newTitleList });
  }
  return {
    existingTexts,
    maxNum,
    setText,
  };
}

/**
 * 智能文案额外信息
 * 作用：用于智能文案工具提交时的埋点(接口形式)上报用，用于智能文案的数据分析
 */
const SmartTextCache: any = {};
export function useSmartTextCache() {
  const cache = computed(() => SmartTextCache);

  function setCache(key, value) {
    if (key === 'requestIds') {
      SmartTextCache[key] = (SmartTextCache[key] || []).concat(value);
      return;
    }
    SmartTextCache[key] = value;
  }

  return {
    cache,
    setCache,
  };
}

const genAcoSmartTextTrackParams = (formData: AcoFormData) => {
  const userTitles = formData.titleList.map(({ title }) => title);
  const selectedTitles = formData.titleList.map(({ title, titleSmartTextInfo }) => {
    const { originTitle = '', requestId = '' } = titleSmartTextInfo || {};

    return { requestId, originTitle, title };
  });
  const requestIds = SmartTextCache.requestIds || [];
  const isClick = requestIds?.length > 0 ? 1 : 0;

  return {
    requestIds,
    selectedTitles,
    userTitles,
    isClick,
  };
};

const genNormalSmartTextTrackParams = (creativeList: NormalCreative[]) => {
  const userTitles: string[] = [];
  const selectedTitles: any[] = [];
  const requestIds = SmartTextCache.requestIds || [];
  const isClick = requestIds?.length > 0 ? 1 : 0;

  for (const creative of creativeList) {
    const { titleSmartTextInfo, title = '' } = getFormDataByNormalCreativeData(creative);
    if (titleSmartTextInfo) {
      const { originTitle = '', requestId = '' } = titleSmartTextInfo;
      userTitles.push(title);
      selectedTitles.push({
        title,
        originTitle,
        requestId,
      });
    }
  }

  return {
    requestIds,
    selectedTitles,
    userTitles,
    isClick,
  };
};

export const genSmartTextTrackParams = (options: {
  acoFormData?: Ref<AcoFormData>;
  isAco?: Ref<boolean>;
  normalCreativeListData?: Ref<NormalCreative[]>;
}) => {
  const { isAco = ref(false), acoFormData = ref({} as AcoFormData), normalCreativeListData = ref([]) } = options;
  const { aadvid } = queryToJson();
  const trackParams = isAco.value ?
    genAcoSmartTextTrackParams(acoFormData.value) :
    genNormalSmartTextTrackParams(normalCreativeListData.value);

  return {
    ...trackParams,
    advId: aadvid,
  };
};
